import { Radio } from "@material-ui/core";
import React, { useEffect, useMemo } from "react";
import CustomDatePicker from "../../../../../../app/components/custom.date.picker";
import { Modal } from "../../../../../../app/components";
import api from "../../../../../../app/utils/api";
import moment from "moment";
import styled from "styled-components";
import { useSnackbar } from "../../../../../../app/contexts/snackbar.context";
import { useStudyPlan } from "../../../../../../app/contexts/study.context";
import PrimaryButton from "../../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../../app/components/buttons/outlined";

const includeExcludeStorageKey = (studyPlanId) =>
  `ctpo-ctis-include-exclude:${studyPlanId}`;

export function normalizeIncludeExclude(value) {
  if (value == null || value === "") {
    return "i";
  }
  const v = String(value).trim().toLowerCase();
  if (v === "e" || v === "exclude") {
    return "e";
  }
  return "i";
}

export function readStoredIncludeExclude(studyPlanId) {
  if (studyPlanId == null) {
    return {};
  }
  try {
    const raw = localStorage.getItem(includeExcludeStorageKey(studyPlanId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function writeStoredIncludeExclude(studyPlanId, siteGroups) {
  if (studyPlanId == null) {
    return;
  }
  try {
    const map = {};
    Object.keys(siteGroups || {}).forEach((id) => {
      map[id] = normalizeIncludeExclude(siteGroups[id]?.includeExclude);
    });
    localStorage.setItem(
      includeExcludeStorageKey(studyPlanId),
      JSON.stringify(map),
    );
  } catch {
    /* ignore */
  }
}

const AddCtisSubmissionModal = ({ open = false, onClose, onSave }) => {
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = React.useState({
    regSubmissionDateUserProvided: "",
    regSubmissionDateType: "old",
    countries: [],
  });
  const { studyPlan } = useStudyPlan();
  const countries = studyPlan.countries;

  const filteredCountries = useMemo(() => {
    const allowedCountries = [
      575, 577, 580, 585, 586, 587, 589, 590, 591, 593, 594, 596, 597, 600, 602,
      604, 605, 606, 612, 614, 619, 620, 621, 625, 626, 629, 630,
    ];
    return countries.filter((c) =>
      allowedCountries.includes(c.siteGroups[0].siteGroupId),
    );
  }, [countries]);

  const disabled = React.useMemo(() => {
    if (form.regSubmissionDateType === "reset") {
      return false;
    }
    if (
      form.regSubmissionDateType === "new" &&
      !form.regSubmissionDateUserProvided
    ) {
      return true;
    }
    return false;
  }, [form.regSubmissionDateUserProvided, form.regSubmissionDateType]);

  useEffect(() => {
    const stored = readStoredIncludeExclude(studyPlan?.studyPlanId);
    const lCountries = {};
    filteredCountries.forEach((country) => {
      const siteGroup = country.siteGroups[0];
      const siteGroupId = siteGroup.siteGroupId;
      lCountries[siteGroupId] = {
        includeExclude: normalizeIncludeExclude(
          siteGroup.includeExclude ??
            siteGroup.IncludeExclude ??
            stored[siteGroupId] ??
            stored[String(siteGroupId)],
        ),
        regSubmissionDate: getDate(country, "YYYY-MM-DD"),
      };
    });
    setForm((prev) => ({ ...prev, siteGroups: lCountries }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCountries, studyPlan?.studyPlanId]);

  const getDate = (country, format = "DD/MMM/yyyy") => {
    const dateMillis = country.siteGroups[0].milestones.find(
      (milestone) => milestone.milestoneId === 135,
    )?.milestoneDateMillis;
    if (!dateMillis) return "";
    return moment(dateMillis).format(format);
  };

  const update = (siteGroupId, value) => {
    setForm((prev) => ({
      ...prev,
      siteGroups: {
        ...prev.siteGroups,
        [siteGroupId]: {
          ...prev.siteGroups[siteGroupId],
          includeExclude: value,
        },
      },
    }));
  };

  const saveCTIS = () => {
    const payload = [];
    Object.keys(form.siteGroups).forEach((siteGroupId) => {
      const siteGroup = form.siteGroups[siteGroupId];
      payload.push({
        studyId: studyPlan.studyId,
        studyPlanId: studyPlan.studyPlanId,
        siteGroupId: siteGroupId ? parseInt(siteGroupId) : null,
        includeExclude: siteGroup.includeExclude,
        regSubmissionDateUserProvided:
          form.regSubmissionDateType === "new"
            ? moment.utc(form.regSubmissionDateUserProvided).format("YYYY-MM-DD")
            : null,
        regSubmissionDate: siteGroup.regSubmissionDate,
        useLatestRegSubmissionsDate: form.regSubmissionDateType === "old",
        enterNewDate: form.regSubmissionDateType === "new",
        resetRegSubmissionDates: form.regSubmissionDateType === "reset",
      });
    });

    api
      .syncSubmissionDates(studyPlan.studyId, payload)
      .then((res) => {
        writeStoredIncludeExclude(studyPlan.studyPlanId, form.siteGroups);
        showSnackbar("CTIS Submission saved successfully", "success");
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch((err) => {
        showSnackbar("CTIS Submission failed", "error");
      });
  };

  return (
    <Modal
      size="xl"
      open={open}
      onClose={onClose}
      aria-labelledby="ctis submission"
      aria-describedby="ctis submission"
      title="CTIS Submission"
    >
      <div style={{ minHeight: "580px" }}>
        <p>
          How would you like to calculate the latest Regulatory Submission date?
        </p>
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <FlexContainer>
            <Radio
              checked={form.regSubmissionDateType === "old"}
              onChange={() =>
                setForm({ ...form, regSubmissionDateType: "old" })
              }
              name="reg-submission-date"
            />
            <label>Use Latest Reg Submission Date</label>
          </FlexContainer>
          <FlexContainer>
            <Radio
              checked={form.regSubmissionDateType === "new"}
              onChange={() =>
                setForm({ ...form, regSubmissionDateType: "new" })
              }
              name="reg-submission-date"
            />
            <label>Enter New Date</label>
          </FlexContainer>
          <FlexContainer>
            <CustomDatePicker
              selected={form.regSubmissionDateUserProvided}
              onChange={(date) =>
                setForm({ ...form, regSubmissionDateUserProvided: date })
              }
              showYearDropdown
              showMonthDropdown
              useShortMonthInDropdown
              dateFormat={"dd/MMM/yyyy"}
              disabled={form.regSubmissionDateType !== "new"}
              placeholderText="DD/MMM/YYYY"
              style={{
                opacity: form.regSubmissionDateType !== "new" ? 0.4 : 1,
                pointerEvents:
                  form.regSubmissionDateType !== "new" ? "none" : "auto",
              }}
            />
          </FlexContainer>
          <FlexContainer>
            <Radio
              checked={form.regSubmissionDateType === "reset"}
              onChange={() =>
                setForm({ ...form, regSubmissionDateType: "reset" })
              }
              name="reg-submission-date"
            />
            <label>Reset Reg Submission Dates</label>
          </FlexContainer>
        </div>
        <Table style={{ marginTop: "2rem" }}>
          <Row>
            <Cell variant="header">Country</Cell>
            <Cell variant="header">Reg Submission Date</Cell>
            <Cell variant="header">Include</Cell>
            <Cell variant="header">Exclude</Cell>
          </Row>
          {filteredCountries.map((country) => {
            const siteGroupId = country.siteGroups[0].siteGroupId;
            const selected = form.siteGroups?.[siteGroupId]?.includeExclude;
            return (
              <Row key={siteGroupId}>
                <Cell>{country.countryName}</Cell>
                <Cell>{getDate(country)}</Cell>
                <Cell>
                  <Radio
                    name={`includeExclude-${siteGroupId}`}
                    value="i"
                    checked={selected === "i"}
                    onChange={() => update(siteGroupId, "i")}
                    inputProps={{ "aria-label": `Include ${country.countryName}` }}
                  />
                </Cell>
                <Cell>
                  <Radio
                    name={`includeExclude-${siteGroupId}`}
                    value="e"
                    checked={selected === "e"}
                    onChange={() => update(siteGroupId, "e")}
                    inputProps={{ "aria-label": `Exclude ${country.countryName}` }}
                  />
                </Cell>
              </Row>
            );
          })}
        </Table>

        <ButtonContainer>
          <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          <PrimaryButton
            disabled={disabled}
            onClick={saveCTIS}
          >
            Continue
          </PrimaryButton>
        </ButtonContainer>
      </div>
    </Modal>
  );
};

export default AddCtisSubmissionModal;

const FlexContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Row = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 50px;
  border-bottom: ${(p) =>
    p.noBorder ? "none" : "1px solid var(--surface-300)"};
  &:last-child {
    border: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-50)" : "white"};
  height: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  width: ${(p) => (p.width ? p.width : "100%")};
  border-right: 1px solid var(--surface-300);
  padding: 5px;
  text-align: center;
  border-left: ${(p) =>
    p.leftBorder ? "1px solid var(--surface-300)" : "none"};
  &:last-child {
    border: ${(p) => (p.rightBorder ? "1px solid var(--surface-300)" : "none")};
  }
  border: ${(p) => (p.fullBorder ? "1px solid var(--surface-300)" : "")};
`;

const Table = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  border: ${(p) => (p.noBorder ? "none" : "1px solid var(--surface-300)")};
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  position: absolute;
  bottom: 2rem;
  width: 97%;
`;
